// Geometry edit reducer cases that operate against cutting-edges /
// boundary-edges (TRIM, EXTEND, FILLET, CHAMFER, BREAK, JOIN,
// LENGTHEN, STRETCH, EXPLODE-ENTITIES). Extracted from
// geometryReducer.ts as part of the PR-0 mechanical decomposition.
//
// Phase 7.2 PR-E2 (#17) — `lineLineIntersection` deshadow.
// The original geometryReducer.ts imported the 4-arg
// `lineLineIntersection(seg1Start, seg1End, seg2Start, seg2End)` from
// `@dxf-ai-editor/command-engine` AND declared a local 2-arg variant
// with the same name (object-segment signature). JS hoisting made
// the local variant shadow the import for the entire module — so the
// 4-arg call sites at TRIM and EXTEND resolved at runtime to the LOCAL
// 2-arg helper, which ignored the 3rd / 4th arg and returned `null`
// on most line-vs-line cases (silent no-op rather than a clean trim).
//
// The shadow has been retired here: the import is renamed to
// `commandLineLineIntersection` and the two TRIM/EXTEND call sites are
// updated to call it explicitly. The local 2-arg variant is removed.
// This is a real behaviour change — TRIM/EXTEND now succeed against
// line cutting/boundary edges in cases where they previously silently
// no-op'd.
//
// Returns `null` when the dispatched command is not handled here so
// the chain in `geometryReducer.ts` falls through to the next
// sub-reducer.
//
// Phase 3b PR #3 — dual export. `applyEditTrimActionWithMutations`
// returns `ReducerMutationResult | null` (per-case mutation tracking).
//
// Phase 6 (Track 1 cleanup) — the legacy `applyEditTrimAction` thin
// wrapper has been removed. `geometryReducer.ts` invokes
// `applyEditTrimActionWithMutations` directly (since PR #5).
import {
  assembleDrawingDocument,
  assembleDrawingDocumentInPlace,
  polylineIsClosed,
  type DrawingDocument,
  type DrawingEntity,
} from "@dxf-ai-editor/drawing-model";
import {
  chamferTwoLines,
  entityIntersectionPoints,
  explodeHeavyPolylineMembers,
  explodePolylineToLines,
  extendArcTarget,
  extendEllipseTarget,
  extendHeavyPolylineTarget,
  extendPolylineTarget,
  extendSplineTarget,
  filletTwoLines,
  lengthenLine,
  pointsMatch,
  selectTrimSurvivors,
  sweepRad,
  trimArcTarget,
  trimCircleTarget,
  trimEllipseTarget,
  trimHeavyPolylineTarget,
  trimPolylineTarget,
  trimSplineTarget,
  type IntPoint,
} from "@dxf-ai-editor/command-engine";
import type {
  DynamicBlockCommand,
  EditorCommand,
} from "@dxf-ai-editor/contracts";

import { explodeBlockInsertInDocument } from "./blockReducerHelpers";
import {
  appendGeometryEntities,
  appendGeometryEntity,
  clearDanglingViewportClips,
  createEntityIdAllocator,
  nextGeometryEntityId,
  removeGeometryEntitiesById,
  replaceGeometryEntityInPlace,
} from "./geometryReducerHelpers";
import {
  NO_MUTATIONS,
  type EntityMutation,
  type ReducerMutationResult,
} from "./types";

/**
 * Mutable accumulator for entity mutations. Final shape returned to
 * the caller is a `ReducerMutationResult` whose `mutations` field is
 * frozen via `freezeMutation`. Using a mutable shell here keeps the
 * branch-local code straightforward (push instead of immutable spread).
 */
interface MutableMutation {
  added: Array<{ spaceId: string; entity: DrawingEntity }>;
  removed: Array<{ spaceId: string; entityId: string }>;
  updated: Array<{
    spaceId: string;
    prev: DrawingEntity;
    next: DrawingEntity;
  }>;
}

function emptyMutable(): MutableMutation {
  return { added: [], removed: [], updated: [] };
}

function freezeMutation(m: MutableMutation): EntityMutation {
  return {
    added: m.added,
    removed: m.removed,
    updated: m.updated,
    styleTablesTouched: NO_MUTATIONS.styleTablesTouched,
  };
}

/**
 * Diff two entity arrays from the same space. Used by the BLOCK arm
 * of EXPLODE where the simplest correctness path is to defer to the
 * document-level helper and reconstruct the mutation from the
 * before/after entity sets. Identity-aware: an entity present in
 * both arrays with the same id but different reference is reported
 * as `updated`. An entity only in `prev` is `removed`. An entity
 * only in `next` is `added`.
 */
function diffSpaceEntities(
  spaceId: string,
  prev: ReadonlyArray<DrawingEntity>,
  next: ReadonlyArray<DrawingEntity>,
  out: MutableMutation,
): void {
  const prevById = new Map<string, DrawingEntity>();
  for (const ent of prev) prevById.set(ent.id, ent);
  const nextById = new Map<string, DrawingEntity>();
  for (const ent of next) nextById.set(ent.id, ent);

  for (const ent of prev) {
    const after = nextById.get(ent.id);
    if (!after) {
      out.removed.push({ spaceId, entityId: ent.id });
    } else if (after !== ent) {
      out.updated.push({ spaceId, prev: ent, next: after });
    }
  }
  for (const ent of next) {
    if (!prevById.has(ent.id)) {
      out.added.push({ spaceId, entity: ent });
    }
  }
}

/**
 * Diff two whole DrawingDocuments space-by-space. Used by EXPLODE
 * (block insert path) where the helper rebuilds the entire document
 * and there's no cheap closed-form way to predict which children
 * were emitted.
 */
function diffDocumentEntities(
  prev: DrawingDocument,
  next: DrawingDocument,
  out: MutableMutation,
): void {
  const nextSpaceById = new Map<string, (typeof next.spaces)[number]>();
  for (const sp of next.spaces) nextSpaceById.set(sp.id, sp);
  for (const prevSpace of prev.spaces) {
    const nextSpace = nextSpaceById.get(prevSpace.id);
    if (!nextSpace) {
      for (const ent of prevSpace.entities) {
        out.removed.push({ spaceId: prevSpace.id, entityId: ent.id });
      }
      continue;
    }
    diffSpaceEntities(
      prevSpace.id,
      prevSpace.entities,
      nextSpace.entities,
      out,
    );
  }
  const prevSpaceIds = new Set(prev.spaces.map((s) => s.id));
  for (const sp of next.spaces) {
    if (prevSpaceIds.has(sp.id)) continue;
    for (const ent of sp.entities) {
      out.added.push({ spaceId: sp.id, entity: ent });
    }
  }
}

// ── MODIFY-OPTIONS (L6) — non-Line TRIM / EXTEND target helpers ────────
//
// AutoCAD TRIM/EXTEND operate on the whole curve family (Line / Arc / Circle /
// Ellipse …) as BOTH the object being cut AND cutting/boundary edges. The
// curve×curve intersection math and the parametric span selection live in the
// command-engine `trimExtendGeometry` module (the CAD core); these thin adapters
// project that math onto the typed `DrawingEntity` model. Convention: **pick =
// remove** — the click identifies the portion to erase (AutoCAD parity + the
// command help "portion to remove"); a mid-curve trim splits into two objects.

const TRIM_GEO_EPS = 1e-9;

/** id prefix for a minted survivor of a given entity type. */
function trimIdPrefix(type: string): string {
  switch (type) {
    case "Line":
      return "line";
    case "Arc":
      return "arc";
    case "Circle":
      return "circle";
    case "Ellipse":
      return "ellipse";
    case "LWPOLYLINE":
      return "pline";
    case "Polyline":
      // DELIBERATELY not "pline": a heavy POLYLINE is a different AutoCAD
      // object from LWPOLYLINE (`model.ts`'s own note on the two type
      // literals), and sharing an id prefix would make two families compete
      // for the same `pline-N` sequence.
      return "polyline";
    case "Spline":
      return "spline";
    default:
      return "entity";
  }
}

/**
 * Did an IN-PLACE (same-id, same-type) TRIM turn a CLOSED clip boundary into an
 * OPEN curve? (Codex #497 P2, extended for MASTERPLAN N3). A full Ellipse OR a
 * CLOSED LWPOLYLINE serving as a non-rectangular VIEWPORT clip is trimmed into
 * an open curve while KEEPING its id — so `clearDanglingViewportClips` (keyed
 * on DELETED ids, like the Circle→Arc type change) never fires for it, leaving
 * the viewport's group-340 pointing at a boundary that is no longer closed
 * (export would re-emit an invalid clip). Only same-type closed→open survivors
 * hit this path; a Circle changes type (Arc) and is handled by the delete
 * path. Returns true so the caller clears dependent clips explicitly.
 */
function closedBoundaryBecameOpen(
  prev: DrawingEntity,
  next: DrawingEntity,
): boolean {
  if (prev.type === "Ellipse" && next.type === "Ellipse") {
    const wasClosed =
      sweepRad(prev.startParam, prev.endParam) >= 2 * Math.PI - TRIM_GEO_EPS;
    const nowClosed =
      sweepRad(next.startParam, next.endParam) >= 2 * Math.PI - TRIM_GEO_EPS;
    return wasClosed && !nowClosed;
  }
  if (prev.type === "LWPOLYLINE" && next.type === "LWPOLYLINE") {
    return prev.closed === true && next.closed !== true;
  }
  if (prev.type === "Polyline" && next.type === "Polyline") {
    // Same rule for the heavy family (@codex PR #785 round 4, P2) — a heavy
    // POLYLINE is just as capable of being a non-rectangular VPCLIP boundary,
    // and its "closed" is DXF group 70 bit 1 rather than a boolean field, so
    // the one-truth `polylineIsClosed` decodes it.
    return polylineIsClosed(prev) && !polylineIsClosed(next);
  }
  return false;
}

/** A rebuilt line endpoint. `z` is threaded through only when the source line
 * carries one on either endpoint, so a 2-D line stays 2-D (no spurious z:0). */
type TrimLinePoint = { x: number; y: number; z?: number };

/** Surviving line param ranges (pick-span removed), or null for a no-op. */
function lineTrimRanges(
  line: Extract<DrawingEntity, { type: "Line" }>,
  cutPoints: IntPoint[],
  pickPoint: IntPoint,
): Array<{ start: TrimLinePoint; end: TrimLinePoint }> | null {
  const sx = line.start.x;
  const sy = line.start.y;
  const dx = line.end.x - sx;
  const dy = line.end.y - sy;
  const len = Math.hypot(dx, dy);
  if (len < TRIM_GEO_EPS) return null;
  const ux = dx / len;
  const uy = dy / len;
  const toParam = (p: IntPoint): number => (p.x - sx) * ux + (p.y - sy) * uy;
  const cuts = cutPoints.map(toParam);
  const pick = toParam(pickPoint);
  const survivors = selectTrimSurvivors(cuts, pick, len, { t0: 0, t1: len });
  if (survivors.length === 0) return null;
  // Codex #497 P2 — preserve / interpolate z. Line endpoints carry optional DXF
  // z (drawingPointOptionalZSchema); rebuilding survivors as {x, y} only flattened
  // an elevated / 3-D line on the next export — even an UNTOUCHED endpoint at
  // s = 0 (start.z) or s = len (end.z) lost its original z. Interpolate along the
  // SAME param the crossings use (2-D projected — the whole TRIM module is
  // XY-planar), which reproduces the exact endpoint z at s = 0 / s = len and a
  // linear z for a mid-split cut. Emit NO z when neither endpoint carried one so a
  // 2-D line stays 2-D.
  const startZ = line.start.z;
  const endZ = line.end.z;
  const hasZ = startZ !== undefined || endZ !== undefined;
  const z0 = startZ ?? 0;
  const z1 = endZ ?? 0;
  const at = (s: number): TrimLinePoint =>
    hasZ
      ? { x: sx + ux * s, y: sy + uy * s, z: z0 + ((z1 - z0) * s) / len }
      : { x: sx + ux * s, y: sy + uy * s };
  return survivors.map(([lo, hi]) => ({ start: at(lo), end: at(hi) }));
}

/**
 * Surviving TRIM geometry as full typed entities cloned from `target` (common
 * props preserved) with a placeholder id. Returns null for a no-op or for a
 * type this slice does not trim as a TARGET (see each arm; every curve family
 * is fully supported as a cutting EDGE).
 */
function computeTrimSurvivors(
  target: DrawingEntity,
  cutPoints: IntPoint[],
  pickPoint: IntPoint,
): DrawingEntity[] | null {
  switch (target.type) {
    case "Line": {
      const ranges = lineTrimRanges(target, cutPoints, pickPoint);
      if (!ranges) return null;
      return ranges.map(
        (r) => ({ ...target, start: r.start, end: r.end }) as DrawingEntity,
      );
    }
    case "Arc": {
      const survivors = trimArcTarget(
        {
          center: target.center,
          radius: target.radius,
          startAngle: target.startAngle,
          endAngle: target.endAngle,
        },
        cutPoints,
        pickPoint,
      );
      if (survivors.length === 0) return null;
      return survivors.map(
        (s) =>
          ({
            ...target,
            startAngle: s.startAngle,
            endAngle: s.endAngle,
          }) as DrawingEntity,
      );
    }
    case "Circle": {
      const arc = trimCircleTarget(
        target.center,
        target.radius,
        cutPoints,
        pickPoint,
      );
      if (!arc) return null;
      // AutoCAD: trimming a circle yields a NEW Arc object (type change).
      // Spread the circle (base + center + radius), retype to Arc, add angles.
      return [
        {
          ...target,
          type: "Arc",
          startAngle: arc.startAngle,
          endAngle: arc.endAngle,
        } as unknown as DrawingEntity,
      ];
    }
    case "Ellipse": {
      const survivors = trimEllipseTarget(target, cutPoints, pickPoint);
      if (survivors.length === 0) return null;
      return survivors.map(
        (s) =>
          ({
            ...target,
            startParam: s.startParam,
            endParam: s.endParam,
          }) as DrawingEntity,
      );
    }
    case "LWPOLYLINE": {
      // MASTERPLAN N3 — segment-level LWPOLYLINE trim. `Revision Cloud`
      // shares the same vertex/bulge shape but is intentionally NOT trimmed
      // here (AutoCAD does not offer TRIM as a target op on a Revcloud —
      // it is edited via its own REVCLOUD command, not MODIFY-OPTIONS).
      const survivors = trimPolylineTarget(
        { vertices: target.vertices, closed: target.closed },
        cutPoints,
        pickPoint,
      );
      if (!survivors) return null;
      return survivors.map(
        (s) => ({ ...target, vertices: s.vertices, closed: false }) as DrawingEntity,
      );
    }
    case "Polyline": {
      // Heavy POLYLINE target (@codex PR #785 round 4, P2). The projection
      // onto the drawn 2D chain, the trim itself and the VERTEX-record
      // write-back are ONE truth in `command-engine`
      // (`heavyPolylineTrimTarget.ts`), which also returns `null` for every
      // subtype `heavyPolylineTrimRefusal` declines in the TARGET role — the
      // shell reducer reports that refusal to the user from the SAME
      // predicate, so the document and the audit row can never disagree.
      const survivors = trimHeavyPolylineTarget(target, cutPoints, pickPoint);
      if (!survivors) return null;
      return survivors as DrawingEntity[];
    }
    case "Spline": {
      // MASTERPLAN N3 — NURBS knot-insertion split. Closed/periodic splines
      // are an explicit, documented follow-up (see `trimExtendSplineTarget.ts`
      // module doc) — `trimSplineTarget` returns null for those, matching
      // this switch's existing "not yet supported as a target" convention.
      const survivors = trimSplineTarget(target, cutPoints, pickPoint);
      if (!survivors) return null;
      return survivors.map(
        (s) =>
          ({
            ...target,
            degree: s.degree,
            controlPoints: s.controlPoints,
            knots: s.knots,
            weights: s.weights ?? [],
            fitPoints: [],
            startTangent: undefined,
            endTangent: undefined,
            closed: false,
          }) as DrawingEntity,
      );
    }
    default:
      return null;
  }
}

/** Extended TRIM/EXTEND target geometry (single in-place patch), or null. */
function computeExtendResult(
  target: DrawingEntity,
  boundaryPoints: IntPoint[],
  pickPoint: IntPoint,
): DrawingEntity | null {
  switch (target.type) {
    case "Line": {
      const sx = target.start.x;
      const sy = target.start.y;
      const dx = target.end.x - sx;
      const dy = target.end.y - sy;
      const len = Math.hypot(dx, dy);
      if (len < TRIM_GEO_EPS) return null;
      const ux = dx / len;
      const uy = dy / len;
      const toParam = (p: IntPoint): number =>
        (p.x - sx) * ux + (p.y - sy) * uy;
      const distToStart = Math.hypot(
        pickPoint.x - target.start.x,
        pickPoint.y - target.start.y,
      );
      const distToEnd = Math.hypot(
        pickPoint.x - target.end.x,
        pickPoint.y - target.end.y,
      );
      const extendEnd = distToEnd <= distToStart;
      let best: number | null = null;
      let bestPt: IntPoint | null = null;
      for (const bp of boundaryPoints) {
        const t = toParam(bp);
        const delta = extendEnd ? t - len : -t; // must land BEYOND the moving end
        if (delta < TRIM_GEO_EPS) continue;
        if (best === null || delta < best) {
          best = delta;
          bestPt = bp;
        }
      }
      if (!bestPt) return null;
      return extendEnd
        ? ({ ...target, end: bestPt } as DrawingEntity)
        : ({ ...target, start: bestPt } as DrawingEntity);
    }
    case "Arc": {
      const a = extendArcTarget(
        {
          center: target.center,
          radius: target.radius,
          startAngle: target.startAngle,
          endAngle: target.endAngle,
        },
        boundaryPoints,
        pickPoint,
      );
      return a
        ? ({ ...target, startAngle: a.startAngle, endAngle: a.endAngle } as DrawingEntity)
        : null;
    }
    case "Circle":
      return null; // a closed circle cannot be extended
    case "Ellipse": {
      const e = extendEllipseTarget(target, boundaryPoints, pickPoint);
      return e
        ? ({ ...target, startParam: e.startParam, endParam: e.endParam } as DrawingEntity)
        : null;
    }
    case "LWPOLYLINE": {
      if (target.closed) return null; // no dangling end to extend
      const grown = extendPolylineTarget(
        { vertices: target.vertices, closed: target.closed },
        boundaryPoints,
        pickPoint,
      );
      return grown ? ({ ...target, vertices: grown.vertices } as DrawingEntity) : null;
    }
    case "Polyline": {
      // Heavy POLYLINE target — see the TRIM arm above. `closed` is DXF group
      // 70 bit 1, decoded inside `extendHeavyPolylineTarget` (a closed chain
      // has no dangling end and returns `null`), so there is no boolean field
      // to test here.
      const grown = extendHeavyPolylineTarget(target, boundaryPoints, pickPoint);
      return grown ? (grown as DrawingEntity) : null;
    }
    case "Spline": {
      if (target.closed) return null;
      const grown = extendSplineTarget(target, boundaryPoints, pickPoint);
      return grown
        ? ({
            ...target,
            degree: grown.degree,
            controlPoints: grown.controlPoints,
            knots: grown.knots,
            weights: grown.weights ?? [],
            fitPoints: [],
            startTangent: undefined,
            endTangent: undefined,
            closed: false,
          } as DrawingEntity)
        : null;
    }
    default:
      return null;
  }
}

/**
 * Phase 3b PR #3 — mutation-aware variant of `applyEditTrimAction`.
 *
 * Returns `ReducerMutationResult { document, mutations }` so the
 * dispatch layer can apply incremental cache + index updates in
 * O(|touched|). Returns `null` when the command is not handled here.
 *
 * Mutation semantics per case:
 * - `geometry/stretch-entities` → `mutations.updated`
 * - `geometry/trim-entity` → `mutations.updated`
 * - `geometry/extend-entity` → `mutations.updated`
 * - `geometry/fillet-entities` → `mutations.updated` (×2 source lines)
 *   plus optional `mutations.added` (fillet arc when radius > 0)
 * - `geometry/chamfer-entities` → `mutations.updated` (×2) + `mutations.added` (chamfer line)
 * - `geometry/break-entity` → `mutations.updated` (kept-id half) + `mutations.added` (other fragment)
 * - `geometry/lengthen-entity` → `mutations.updated`
 * - `geometry/join-entities` → `mutations.removed` (sources) + `mutations.added` (polyline)
 * - `geometry/explode-entities` → `mutations.removed` (sources) + `mutations.added` (members)
 */
export function applyEditTrimActionWithMutations(
  geometryDocument: DrawingDocument,
  command: EditorCommand | DynamicBlockCommand,
): ReducerMutationResult | null {
  switch (command.type) {
    // ── STRETCH ─────────────────────────────────────────────────────
    case "geometry/stretch-entities": {
      const { entityIds, stretchWindow, delta } = command;
      const idsToStretch = new Set(entityIds);
      const { x1, y1, x2, y2 } = stretchWindow;
      const minX = Math.min(x1, x2);
      const maxX = Math.max(x1, x2);
      const minY = Math.min(y1, y2);
      const maxY = Math.max(y1, y2);

      function isInsideWindow(p: { x: number; y: number }): boolean {
        return p.x >= minX && p.x <= maxX && p.y >= minY && p.y <= maxY;
      }

      function stretchPoint(p: { x: number; y: number }): {
        x: number;
        y: number;
      } {
        if (isInsideWindow(p)) {
          return { x: p.x + delta.x, y: p.y + delta.y };
        }
        return p;
      }

      const mut = emptyMutable();
      // T6c incremental validation: STRETCH is a pure in-place per-entity
      // transform — no entity added/removed, no id/spaceId change → invariants
      // preserved by construction. Collect the actually-mutated ids (a matched
      // entity may no-op, e.g. an unsupported type, leaving `nextEntity ===
      // entity`) and re-parse ONLY those in dev/test via the fast-path
      // assembler. PROD skips the O(N) walk.
      const touchedIds: string[] = [];

      const document = assembleDrawingDocumentInPlace(
        {
          ...geometryDocument,
          spaces: geometryDocument.spaces.map((space) => ({
            ...space,
            entities: space.entities.map((entity) => {
              if (!idsToStretch.has(entity.id)) return entity;

              let nextEntity: DrawingEntity = entity;
              switch (entity.type) {
                case "Line":
                  nextEntity = {
                    ...entity,
                    start: stretchPoint(entity.start),
                    end: stretchPoint(entity.end),
                  };
                  break;
                case "Circle":
                case "Arc":
                  nextEntity = {
                    ...entity,
                    center: stretchPoint(entity.center),
                  };
                  break;
                case "LWPOLYLINE":
                  nextEntity = {
                    ...entity,
                    vertices: entity.vertices.map((v) => {
                      const sp = stretchPoint({ x: v.x, y: v.y });
                      return { ...v, x: sp.x, y: sp.y };
                    }),
                  };
                  break;
                case "Text":
                  nextEntity = {
                    ...entity,
                    insertionPoint: stretchPoint(entity.insertionPoint),
                  };
                  break;
                case "MText":
                  nextEntity = {
                    ...entity,
                    insertionPoint: stretchPoint(entity.insertionPoint),
                  };
                  break;
                case "Block":
                  nextEntity = {
                    ...entity,
                    insertionPoint: stretchPoint(entity.insertionPoint),
                  };
                  break;
                case "Dimension":
                  nextEntity = {
                    ...entity,
                    defPoint: stretchPoint(entity.defPoint),
                    textMidpoint: stretchPoint(entity.textMidpoint),
                    extLine1Point: entity.extLine1Point
                      ? stretchPoint(entity.extLine1Point)
                      : undefined,
                    extLine2Point: entity.extLine2Point
                      ? stretchPoint(entity.extLine2Point)
                      : undefined,
                  };
                  break;
                case "Multileader":
                  nextEntity = {
                    ...entity,
                    textLocation: stretchPoint(entity.textLocation),
                    leaderRoots: entity.leaderRoots.map((root) => ({
                      ...root,
                      nodes: root.nodes.map((node) => ({
                        ...node,
                        lines: node.lines.map((line) => ({
                          ...line,
                          vertices: line.vertices.map((v) => stretchPoint(v)),
                        })),
                      })),
                    })),
                  };
                  break;
                case "Hatch":
                  nextEntity = {
                    ...entity,
                    boundaryPaths: entity.boundaryPaths.map((path) => ({
                      ...path,
                      edges: path.edges.map((edge) => {
                        switch (edge.edgeType) {
                          case "line":
                            return {
                              ...edge,
                              start: stretchPoint(edge.start),
                              end: stretchPoint(edge.end),
                            };
                          case "arc":
                            return {
                              ...edge,
                              center: stretchPoint(edge.center),
                            };
                          case "ellipticArc":
                            return {
                              ...edge,
                              center: stretchPoint(edge.center),
                            };
                          default:
                            return edge;
                        }
                      }),
                    })),
                    seedPoints: entity.seedPoints.map((sp) => stretchPoint(sp)),
                  };
                  break;
                case "Revision Cloud":
                  nextEntity = {
                    ...entity,
                    vertices: entity.vertices.map((v) => {
                      const sp = stretchPoint({ x: v.x, y: v.y });
                      return { ...v, x: sp.x, y: sp.y };
                    }),
                  };
                  break;
                case "Viewport":
                  nextEntity = {
                    ...entity,
                    center: stretchPoint(entity.center),
                  };
                  break;
                case "Rectangular Associative Array":
                  nextEntity = {
                    ...entity,
                    basePoint: stretchPoint(entity.basePoint),
                  };
                  break;
                default:
                  nextEntity = entity;
              }

              if (nextEntity !== entity) {
                touchedIds.push(entity.id);
                mut.updated.push({
                  spaceId: space.id,
                  prev: entity,
                  next: nextEntity,
                });
              }
              return nextEntity;
            }),
          })),
        },
        touchedIds,
      );

      return { document, mutations: freezeMutation(mut) };
    }

    // ── TRIM ────────────────────────────────────────────────────────
    // AutoCAD parity: any Line/Arc/Circle/Ellipse/LWPOLYLINE/Spline target may
    // be cut against any Line/Circle/Arc/Ellipse/LWPOLYLINE/Spline cutting edge
    // (MASTERPLAN N3 closed the LWPOLYLINE/Spline TARGET gap — closed/periodic
    // Spline targets remain a documented follow-up, see
    // `trimExtendSplineTarget.ts`). The pick point identifies the portion to
    // REMOVE (a mid-curve trim splits into two).
    case "geometry/trim-entity": {
      const { entityId, cuttingEdgeIds, pickPoint } = command;
      const cuttingEdgeSet = new Set(cuttingEdgeIds);

      const cuttingEdges: DrawingEntity[] = [];
      let target: DrawingEntity | null = null;
      let targetSpaceId = "";
      for (const space of geometryDocument.spaces) {
        for (const entity of space.entities) {
          if (cuttingEdgeSet.has(entity.id)) cuttingEdges.push(entity);
          if (entity.id === entityId) {
            target = entity;
            targetSpaceId = space.id;
          }
        }
      }
      if (!target) {
        return { document: geometryDocument, mutations: NO_MUTATIONS };
      }

      // Cut points = real crossings of the target's actual extent (edge extent
      // respected — AutoCAD default Edge = No extend).
      const cutPoints: IntPoint[] = [];
      for (const edge of cuttingEdges) {
        if (edge.id === entityId) continue;
        for (const pt of entityIntersectionPoints(target, edge)) {
          cutPoints.push(pt);
        }
      }
      if (cutPoints.length === 0) {
        return { document: geometryDocument, mutations: NO_MUTATIONS };
      }

      const survivors = computeTrimSurvivors(target, cutPoints, pickPoint);
      if (!survivors || survivors.length === 0) {
        return { document: geometryDocument, mutations: NO_MUTATIONS };
      }

      const mut = emptyMutable();
      const firstSameType = survivors[0]!.type === target.type;

      if (firstSameType) {
        // In-place shorten (survivors[0] keeps the target id); any additional
        // survivor (mid-curve split) is minted as a new entity.
        const updatedEntity = {
          ...survivors[0]!,
          id: target.id,
          spaceId: targetSpaceId,
        } as DrawingEntity;
        // Mint ids for any mid-curve split survivors. Seeding the allocator from
        // the PRE-edit document is byte-identical to seeding after the in-place
        // replace: survivors[0] keeps the target id AND type, so neither the
        // live-id set nor the per-type population the allocator counts from
        // changes.
        //
        // Associative-array membership (`arrayGroupId`/`arraySourceOfGroup`/
        // `arrayMode`) is DELIBERATELY left untouched here, inherited as-is
        // from `target` via `computeTrimSurvivors`'s own `{...target,
        // ...changes}` spread (Codex P2, GitHub review, round 3 — supersedes
        // two EARLIER "strip the extra fragment's tags" attempts this same
        // finding went through): the array system's own `entityIds`/
        // `sourceIds` plumbing (`collectSourceEntities`, `handleCreateArray
        // Rect`/`Polar`/`Path`'s `for (const entity of taggedSourceEntities)`
        // inner loop, `handleArrayEdit`'s own doc comment) is explicitly
        // designed to support a MULTI-ENTITY source/copy set per group —
        // arraying several entities together as one rigid unit is a first-
        // class, pre-existing case. Stripping the tag from the newly-minted
        // fragment dropped it out of the group's `sourceIds`/copy-sweep set
        // entirely, so the next ARRAYEDIT only regenerated the id-preserving
        // half, leaving the split-off other half sitting once at the origin
        // instead of being replicated across the array with the rest of the
        // (now multi-piece) source/copy shape. Letting BOTH fragments keep
        // the IDENTICAL tag is exactly how this codebase already represents
        // "this array member is more than one entity" — not a "duplicate
        // source" bug.
        const extras = survivors.slice(1);
        const alloc =
          extras.length > 0 ? createEntityIdAllocator(geometryDocument) : null;
        const extraEntities = extras.map(
          (s) =>
            ({
              ...s,
              id: alloc!.next(trimIdPrefix(s.type)),
              spaceId: targetSpaceId,
            }) as DrawingEntity,
        );
        // Draw-order fix (Codex #497 P2): splice the survivor + split fragments
        // into the trimmed entity's slot instead of appending fragments to the
        // space tail (which would render / export them above later entities).
        let nextDoc = replaceGeometryEntityInPlace(
          geometryDocument,
          targetSpaceId,
          target.id,
          [updatedEntity, ...extraEntities],
        );
        mut.updated.push({
          spaceId: targetSpaceId,
          prev: target,
          next: updatedEntity,
        });
        for (const ex of extraEntities) {
          mut.added.push({ spaceId: targetSpaceId, entity: ex });
        }
        // Codex #497 P2 — a CLOSED clip boundary (a full Ellipse serving as a
        // non-rectangular VIEWPORT clip) trimmed into an OPEN elliptic arc keeps
        // its id, so `clearDanglingViewportClips` (keyed on DELETED ids) never
        // fires for it the way the Circle→Arc delete path does. The viewport's
        // group-340 would still point at a boundary that is no longer closed and
        // export would re-emit an invalid clip. Clear dependent clips whenever a
        // closed boundary becomes open in place, threading each per-VP diff into
        // `mutations.updated` (incremental compact-cache parity).
        if (closedBoundaryBecameOpen(target, updatedEntity)) {
          const { document: cleanedDoc, updated: clipUpdated } =
            clearDanglingViewportClips(nextDoc, [target.id]);
          for (const u of clipUpdated) {
            mut.updated.push(u);
          }
          nextDoc = cleanedDoc;
        }
        return { document: nextDoc, mutations: freezeMutation(mut) };
      }

      // Type change (Circle → Arc): mint the replacement Arc(s) and SPLICE them
      // into the trimmed Circle's draw-order slot (Codex #497 P2 — a tail append
      // would render / export the Arc above entities originally drawn after the
      // Circle). Seeding the allocator from the PRE-edit document is
      // byte-identical for the minted "arc" ids: the removed Circle can never
      // collide with an "arc-N" candidate and the "arc" population is unchanged.
      const alloc = createEntityIdAllocator(geometryDocument);
      const addedEntities = survivors.map(
        (s) =>
          ({
            ...s,
            id: alloc.next(trimIdPrefix(s.type)),
            spaceId: targetSpaceId,
          }) as DrawingEntity,
      );
      const nextDoc = replaceGeometryEntityInPlace(
        geometryDocument,
        targetSpaceId,
        target.id,
        addedEntities,
      );
      mut.removed.push({ spaceId: targetSpaceId, entityId: target.id });
      for (const a of addedEntities) {
        mut.added.push({ spaceId: targetSpaceId, entity: a });
      }
      // Codex #497 P2 — referential cleanup (mirrors the erase reducers'
      // `clearDanglingViewportClips`). The removed Circle keeps its id while the
      // minted Arc gets a NEW id, so if that circle was a paperspace VPCLIP
      // boundary a viewport's group-340 `clippingBoundaryHandle` now dangles at a
      // deleted id and export would re-emit an invalid 340. Clear the dangling
      // clip and thread each per-VP diff into `mutations.updated` so the
      // incremental compact-cache drops the stale handle too (not just the
      // returned document).
      const { document: cleanedDoc, updated: clipUpdated } =
        clearDanglingViewportClips(nextDoc, [target.id]);
      for (const u of clipUpdated) {
        mut.updated.push(u);
      }
      return { document: cleanedDoc, mutations: freezeMutation(mut) };
    }

    // ── EXTEND ─────────────────────────────────────────────────────
    // The target's near end (closest to the pick) grows out to the nearest
    // boundary crossing that lies BEYOND it. The target is intersected as its
    // infinite carrier so landing points past the current ends are found.
    case "geometry/extend-entity": {
      const { entityId, boundaryEdgeIds, pickPoint } = command;
      const boundarySet = new Set(boundaryEdgeIds);

      const boundaries: DrawingEntity[] = [];
      let target: DrawingEntity | null = null;
      let targetSpaceId = "";
      for (const space of geometryDocument.spaces) {
        for (const entity of space.entities) {
          if (boundarySet.has(entity.id)) boundaries.push(entity);
          if (entity.id === entityId) {
            target = entity;
            targetSpaceId = space.id;
          }
        }
      }
      if (!target) {
        return { document: geometryDocument, mutations: NO_MUTATIONS };
      }

      const boundaryPoints: IntPoint[] = [];
      for (const edge of boundaries) {
        if (edge.id === entityId) continue;
        for (const pt of entityIntersectionPoints(target, edge, {
          targetInfinite: true,
        })) {
          boundaryPoints.push(pt);
        }
      }
      if (boundaryPoints.length === 0) {
        return { document: geometryDocument, mutations: NO_MUTATIONS };
      }

      const nextGeom = computeExtendResult(target, boundaryPoints, pickPoint);
      if (!nextGeom) {
        return { document: geometryDocument, mutations: NO_MUTATIONS };
      }

      const nextEntity = {
        ...nextGeom,
        id: target.id,
        spaceId: targetSpaceId,
      } as DrawingEntity;
      const mut = emptyMutable();
      const document = assembleDrawingDocument({
        ...geometryDocument,
        spaces: geometryDocument.spaces.map((space) => ({
          ...space,
          entities: space.entities.map((entity) =>
            entity.id === target!.id ? nextEntity : entity,
          ),
        })),
      });
      mut.updated.push({
        spaceId: targetSpaceId,
        prev: target,
        next: nextEntity,
      });
      return { document, mutations: freezeMutation(mut) };
    }

    // ── FILLET ──────────────────────────────────────────────────────
    case "geometry/fillet-entities": {
      const { entityId1, entityId2, radius, pickPoint1, pickPoint2 } = command;

      let ent1: DrawingEntity | null = null;
      let ent2: DrawingEntity | null = null;
      for (const space of geometryDocument.spaces) {
        for (const entity of space.entities) {
          if (entity.id === entityId1) ent1 = entity;
          if (entity.id === entityId2) ent2 = entity;
        }
      }

      if (!ent1 || !ent2 || ent1.type !== "Line" || ent2.type !== "Line") {
        return { document: geometryDocument, mutations: NO_MUTATIONS };
      }

      const result = filletTwoLines(
        ent1.start,
        ent1.end,
        ent2.start,
        ent2.end,
        radius,
        pickPoint1,
        pickPoint2,
      );

      const mut = emptyMutable();
      const prevEnt1 = ent1;
      const prevEnt2 = ent2;
      const ent1SpaceId = ent1.spaceId;

      let nextDoc = assembleDrawingDocument({
        ...geometryDocument,
        spaces: geometryDocument.spaces.map((space) => ({
          ...space,
          entities: space.entities.map((entity) => {
            if (entity.id === entityId1 && entity.type === "Line") {
              const next = {
                ...entity,
                start: result.line1.start,
                end: result.line1.end,
              };
              mut.updated.push({
                spaceId: space.id,
                prev: prevEnt1,
                next,
              });
              return next;
            }
            if (entity.id === entityId2 && entity.type === "Line") {
              const next = {
                ...entity,
                start: result.line2.start,
                end: result.line2.end,
              };
              mut.updated.push({
                spaceId: space.id,
                prev: prevEnt2,
                next,
              });
              return next;
            }
            return entity;
          }),
        })),
      });

      if (result.arc) {
        const arcEntity: DrawingEntity = {
          id: nextGeometryEntityId(nextDoc, "arc"),
          type: "Arc",
          spaceId: ent1SpaceId,
          center: result.arc.center,
          radius: result.arc.radius,
          startAngle: result.arc.startAngle,
          endAngle: result.arc.endAngle,
        };
        nextDoc = appendGeometryEntity(nextDoc, arcEntity);
        mut.added.push({ spaceId: ent1SpaceId, entity: arcEntity });
      }

      return { document: nextDoc, mutations: freezeMutation(mut) };
    }

    // ── CHAMFER ────────────────────────────────────────────────────
    case "geometry/chamfer-entities": {
      const { entityId1, entityId2, dist1, dist2 } = command;

      let ent1: DrawingEntity | null = null;
      let ent2: DrawingEntity | null = null;
      for (const space of geometryDocument.spaces) {
        for (const entity of space.entities) {
          if (entity.id === entityId1) ent1 = entity;
          if (entity.id === entityId2) ent2 = entity;
        }
      }

      if (!ent1 || !ent2 || ent1.type !== "Line" || ent2.type !== "Line") {
        return { document: geometryDocument, mutations: NO_MUTATIONS };
      }

      const result = chamferTwoLines(
        ent1.start,
        ent1.end,
        ent2.start,
        ent2.end,
        dist1,
        dist2,
      );

      if (!result) {
        return { document: geometryDocument, mutations: NO_MUTATIONS };
      }

      const mut = emptyMutable();
      const prevEnt1 = ent1;
      const prevEnt2 = ent2;
      const ent1SpaceId = ent1.spaceId;

      let nextDoc = assembleDrawingDocument({
        ...geometryDocument,
        spaces: geometryDocument.spaces.map((space) => ({
          ...space,
          entities: space.entities.map((entity) => {
            if (entity.id === entityId1 && entity.type === "Line") {
              const next = {
                ...entity,
                start: result.line1.start,
                end: result.line1.end,
              };
              mut.updated.push({
                spaceId: space.id,
                prev: prevEnt1,
                next,
              });
              return next;
            }
            if (entity.id === entityId2 && entity.type === "Line") {
              const next = {
                ...entity,
                start: result.line2.start,
                end: result.line2.end,
              };
              mut.updated.push({
                spaceId: space.id,
                prev: prevEnt2,
                next,
              });
              return next;
            }
            return entity;
          }),
        })),
      });

      const chamferLineEntity: DrawingEntity = {
        id: nextGeometryEntityId(nextDoc, "line"),
        type: "Line",
        spaceId: ent1SpaceId,
        start: result.chamferLine.start,
        end: result.chamferLine.end,
      };
      nextDoc = appendGeometryEntity(nextDoc, chamferLineEntity);
      mut.added.push({ spaceId: ent1SpaceId, entity: chamferLineEntity });
      return { document: nextDoc, mutations: freezeMutation(mut) };
    }

    // ── BREAK ──────────────────────────────────────────────────────
    case "geometry/break-entity": {
      const { entityId, point1, point2 } = command;

      const mut = emptyMutable();
      let touchedSpaceId: string | null = null;
      let originalEntity: DrawingEntity | null = null;
      let resultingEntities: DrawingEntity[] = [];

      const document = assembleDrawingDocument({
        ...geometryDocument,
        spaces: geometryDocument.spaces.map((space) => {
          const targetIdx = space.entities.findIndex((e) => e.id === entityId);
          if (targetIdx === -1) return space;
          const entity = space.entities[targetIdx];
          if (entity.type !== "Line") return space;

          const lineDir = {
            x: entity.end.x - entity.start.x,
            y: entity.end.y - entity.start.y,
          };
          const lineLen = Math.hypot(lineDir.x, lineDir.y);
          if (lineLen < 1e-14) return space;

          const t1 =
            ((point1.x - entity.start.x) * lineDir.x +
              (point1.y - entity.start.y) * lineDir.y) /
            (lineLen * lineLen);
          const t2 =
            ((point2.x - entity.start.x) * lineDir.x +
              (point2.y - entity.start.y) * lineDir.y) /
            (lineLen * lineLen);

          const tMin = Math.max(0, Math.min(t1, t2));
          const tMax = Math.min(1, Math.max(t1, t2));

          if (tMin >= 1 || tMax <= 0) return space;

          const breakPt1 = {
            x: entity.start.x + tMin * lineDir.x,
            y: entity.start.y + tMin * lineDir.y,
          };
          const breakPt2 = {
            x: entity.start.x + tMax * lineDir.x,
            y: entity.start.y + tMax * lineDir.y,
          };

          const newEntities: DrawingEntity[] = [];

          if (tMin > 0.001) {
            newEntities.push({
              ...entity,
              id: entity.id,
              end: breakPt1,
            });
          }

          if (tMax < 0.999) {
            newEntities.push({
              ...entity,
              id: nextGeometryEntityId(geometryDocument, "line"),
              start: breakPt2,
            });
          }

          touchedSpaceId = space.id;
          originalEntity = entity;
          resultingEntities = newEntities;

          const nextEntities = [...space.entities];
          nextEntities.splice(targetIdx, 1, ...newEntities);
          return { ...space, entities: nextEntities };
        }),
      });

      if (touchedSpaceId !== null && originalEntity !== null) {
        const origEnt = originalEntity as DrawingEntity;
        const origId = origEnt.id;
        const keptSelf = resultingEntities.find((e) => e.id === origId);
        const others = resultingEntities.filter((e) => e.id !== origId);
        if (keptSelf) {
          mut.updated.push({
            spaceId: touchedSpaceId,
            prev: origEnt,
            next: keptSelf,
          });
        } else {
          // D4 fix — no surviving fragment carries the original id, either
          // because the ONLY surviving fragment was minted with a NEW id
          // (left fragment below the 0.001 survival threshold — handled by
          // the `others` loop below) OR because BOTH fragments were below
          // threshold and the break consumed the entire entity
          // (`resultingEntities.length === 0`). Either way the original id
          // no longer exists in the document, so it MUST be recorded as
          // `removed` — previously this branch was gated on
          // `resultingEntities.length > 0`, so the whole-entity-consumed case
          // recorded NOTHING even though the document actually dropped the
          // entity (a silent mutation-tracking gap that defeated BREAK's
          // snapshot-based undo — see computeInverse's
          // `buildModifyCommandSnapshotInverse` — for that edge case, and
          // left the incremental cache/index pointing at a deleted entity).
          mut.removed.push({
            spaceId: touchedSpaceId,
            entityId: origId,
          });
        }
        for (const ent of others) {
          mut.added.push({ spaceId: touchedSpaceId, entity: ent });
        }
      }

      return { document, mutations: freezeMutation(mut) };
    }

    // ── JOIN ───────────────────────────────────────────────────────
    case "geometry/join-entities": {
      const { entityIds } = command;
      const idSet = new Set(entityIds);

      const linesToJoin: Array<DrawingEntity & { type: "Line" }> = [];
      let targetSpaceId = "";
      for (const space of geometryDocument.spaces) {
        for (const entity of space.entities) {
          if (idSet.has(entity.id) && entity.type === "Line") {
            linesToJoin.push(entity as DrawingEntity & { type: "Line" });
            if (!targetSpaceId) targetSpaceId = entity.spaceId;
          }
        }
      }

      if (linesToJoin.length < 2) {
        return { document: geometryDocument, mutations: NO_MUTATIONS };
      }

      const chain: Array<{ x: number; y: number }> = [];
      const used = new Set<string>();

      const first = linesToJoin[0];
      chain.push(first.start, first.end);
      used.add(first.id);

      let extended = true;
      while (extended) {
        extended = false;
        for (const line of linesToJoin) {
          if (used.has(line.id)) continue;
          const chainStart = chain[0];
          const chainEnd = chain[chain.length - 1];

          if (pointsMatch(line.start, chainEnd)) {
            chain.push(line.end);
            used.add(line.id);
            extended = true;
          } else if (pointsMatch(line.end, chainEnd)) {
            chain.push(line.start);
            used.add(line.id);
            extended = true;
          } else if (pointsMatch(line.end, chainStart)) {
            chain.unshift(line.start);
            used.add(line.id);
            extended = true;
          } else if (pointsMatch(line.start, chainStart)) {
            chain.unshift(line.end);
            used.add(line.id);
            extended = true;
          }
        }
      }

      const isClosed = pointsMatch(chain[0], chain[chain.length - 1]);
      const vertices = (isClosed ? chain.slice(0, -1) : chain).map((p) => ({
        x: p.x,
        y: p.y,
        bulge: 0,
        startWidth: 0,
        endWidth: 0,
      }));

      const polyEntity: DrawingEntity = {
        id: nextGeometryEntityId(geometryDocument, "pline"),
        type: "LWPOLYLINE",
        spaceId: targetSpaceId,
        vertices,
        closed: isClosed,
      };

      const mut = emptyMutable();
      const removedLines = linesToJoin.filter((l) => used.has(l.id));
      for (const line of removedLines) {
        mut.removed.push({ spaceId: line.spaceId, entityId: line.id });
      }
      mut.added.push({ spaceId: targetSpaceId, entity: polyEntity });

      let nextDoc = removeGeometryEntitiesById(
        geometryDocument,
        removedLines.map((l) => l.id),
      );
      nextDoc = appendGeometryEntity(nextDoc, polyEntity);
      return { document: nextDoc, mutations: freezeMutation(mut) };
    }

    // ── EXPLODE ────────────────────────────────────────────────────
    case "geometry/explode-entities": {
      const { entityIds } = command;
      const idSet = new Set(entityIds);

      let nextDoc = geometryDocument;
      const mut = emptyMutable();

      for (const space of geometryDocument.spaces) {
        for (const entity of space.entities) {
          if (!idSet.has(entity.id)) continue;

          if (entity.type === "LWPOLYLINE") {
            const lineSegments = explodePolylineToLines(
              entity.vertices,
              entity.closed,
              entity.spaceId,
              nextGeometryEntityId(nextDoc, "line"),
            );
            nextDoc = removeGeometryEntitiesById(nextDoc, [entity.id]);
            mut.removed.push({ spaceId: entity.spaceId, entityId: entity.id });
            // T6a perf: mint each segment's "line" id via an allocator seeded
            // ONCE from the post-removal doc (amortized O(1), reproducing the
            // per-call nextGeometryEntityId sequence — the first segment sees the
            // same post-removal Line count the per-call path saw), and append the
            // whole segment batch with ONE assembleDrawingDocument pass instead
            // of O(N) per segment (O(N·segs) → O(N)). Re-seeded per polyline so a
            // prior Block-explode that injected Line-typed children is reflected
            // in the counter base — keeping byte-identity for mixed batches.
            const segIdAllocator = createEntityIdAllocator(nextDoc);
            const segEntities: DrawingEntity[] = [];
            for (let i = 0; i < lineSegments.length; i++) {
              const seg = lineSegments[i];
              segEntities.push({
                id: segIdAllocator.next("line"),
                type: "Line",
                spaceId: seg.spaceId,
                start: seg.start,
                end: seg.end,
              });
            }
            nextDoc = appendGeometryEntities(nextDoc, segEntities);
            for (const lineEntity of segEntities) {
              mut.added.push({
                spaceId: lineEntity.spaceId,
                entity: lineEntity,
              });
            }
          } else if (entity.type === "Polyline") {
            // TOP-LEVEL heavy POLYLINE (@codex PR #785 round 14, FINDING 2).
            // Before this arm the entity fell out of the `if` chain untouched
            // while the dispatch still reported success — a silent no-op on a
            // primitive the same PR had just made selectable. The LINE / ARC
            // decomposition, the per-subtype AutoCAD rule it implements and the
            // VISIBLE refusal for the two mesh subtypes are ONE truth in
            // `command-engine/explodeHeavyPolyline.ts`; the shell reducer reads
            // the SAME predicate to write the decline row, so it can never
            // report a refusal this arm did not make.
            //
            // `[]` means the polyline was DECLINED (a polygon / poly-face mesh,
            // which AutoCAD explodes into 3DFACE records this editor has no
            // primitive for, or a run with no drawable span). The entity is then
            // left exactly as it was — EXPLODE must never remove an object it
            // did not replace.
            const memberIdAllocator = createEntityIdAllocator(nextDoc);
            const members = explodeHeavyPolylineMembers(entity, (prefix) =>
              memberIdAllocator.next(prefix),
            );
            if (members.length === 0) continue;
            nextDoc = removeGeometryEntitiesById(nextDoc, [entity.id]);
            mut.removed.push({
              spaceId: entity.spaceId,
              entityId: entity.id,
            });
            nextDoc = appendGeometryEntities(nextDoc, members);
            for (const member of members) {
              mut.added.push({ spaceId: member.spaceId, entity: member });
            }
          } else if (entity.type === "Block") {
            const blockDef = nextDoc.blocks[entity.blockName];
            const blockRecord = nextDoc.blockRecords[entity.blockName];
            if (blockDef && blockRecord?.explodable !== false) {
              const before = nextDoc;
              nextDoc = explodeBlockInsertInDocument(nextDoc, entity, blockDef);
              diffDocumentEntities(before, nextDoc, mut);
            } else {
              nextDoc = removeGeometryEntitiesById(nextDoc, [entity.id]);
              mut.removed.push({
                spaceId: entity.spaceId,
                entityId: entity.id,
              });
            }
          }
        }
      }

      return { document: nextDoc, mutations: freezeMutation(mut) };
    }

    // ── LENGTHEN ───────────────────────────────────────────────────
    case "geometry/lengthen-entity": {
      const { entityId, mode, value, endPoint } = command;
      const mut = emptyMutable();

      const document = assembleDrawingDocument({
        ...geometryDocument,
        spaces: geometryDocument.spaces.map((space) => ({
          ...space,
          entities: space.entities.map((entity) => {
            if (entity.id !== entityId) return entity;
            if (entity.type !== "Line") return entity;

            const result = lengthenLine(
              entity.start,
              entity.end,
              mode,
              value,
              endPoint,
            );
            const next = { ...entity, start: result.start, end: result.end };
            mut.updated.push({
              spaceId: space.id,
              prev: entity,
              next,
            });
            return next;
          }),
        })),
      });

      return { document, mutations: freezeMutation(mut) };
    }

    default:
      return null;
  }
}
